/**
 * MilestoneToast — pop từ trên xuống 4s khi unlock milestone.
 *
 * Đọc từ `cafe3dStore.milestoneQueue`, dismiss sau timeout hoặc click.
 *
 * Tham chiếu:
 *   - `docs/analysis/02_use_cases.md` UC-06 §7.2 step 12.
 *   - design/01_main_cafe_scene_design.md §11.
 */
import { AnimatePresence, motion } from "framer-motion";
import { useEffect } from "react";

import { useCafe3dStore } from "../stores/cafe3dStore";

const MILESTONE_LABEL: Record<string, string> = {
  first_sale: "Bán được ly đầu tiên",
  first_upgrade: "Mua nâng cấp đầu tiên",
  hundred_sales: "100 lượt bán đầu tiên",
};

const AUTO_DISMISS_MS = 4000;

export function MilestoneToast() {
  const queue = useCafe3dStore((s) => s.milestoneQueue);
  const dismiss = useCafe3dStore((s) => s.dismissMilestone);
  const current = queue[0] ?? null;

  useEffect(() => {
    if (!current) return;
    const t = setTimeout(() => dismiss(), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [current, dismiss]);

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current.code}
          initial={{ y: -120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 280, damping: 22 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] max-w-sm w-[90%]"
        >
          <button
            type="button"
            onClick={dismiss}
            className="w-full bg-gradient-to-r from-yellow-400 to-amber50 text-coffee-900 rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3 hover:brightness-105 active:scale-95 transition"
            data-testid="milestone-toast"
          >
            <span className="text-3xl">🏆</span>
            <div className="flex-1 text-left">
              <div className="text-xs font-medium opacity-80">
                Cột mốc đã đạt
              </div>
              <div className="font-bold font-baloo text-base">
                {MILESTONE_LABEL[current.code] ?? current.code}
              </div>
              {current.rewardBean > 0 && (
                <div className="text-sm font-baloo">
                  +{current.rewardBean.toLocaleString("vi-VN")} ☕
                </div>
              )}
            </div>
            <span className="text-xs opacity-60">×</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
